// FeaturedContent - Horizontal scrolling section for featured articles

import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Sparkles } from 'lucide-react';
import { getFeaturedArticles, type Article } from '../../services/articlesService';
import { ArticleCard } from './ArticleCard';

interface FeaturedContentProps {
  onArticleClick?: (article: Article) => void;
  maxArticles?: number;
}

export function FeaturedContent({ onArticleClick, maxArticles = 5 }: FeaturedContentProps) {
  const [articles, setArticles] = useState<Article[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  useEffect(() => {
    loadArticles();
  }, [maxArticles]);

  const loadArticles = async () => {
    try {
      console.log('[FeaturedContent] Loading featured articles...');
      const featuredArticles = await getFeaturedArticles(maxArticles);
      console.log('[FeaturedContent] Loaded articles:', featuredArticles.length, featuredArticles);
      setArticles(featuredArticles);
    } catch (error) {
      console.error('[FeaturedContent] Error loading featured articles:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const checkScrollButtons = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  useEffect(() => {
    checkScrollButtons();
    const scrollEl = scrollRef.current;
    if (scrollEl) {
      scrollEl.addEventListener('scroll', checkScrollButtons);
      window.addEventListener('resize', checkScrollButtons);
      return () => {
        scrollEl.removeEventListener('scroll', checkScrollButtons);
        window.removeEventListener('resize', checkScrollButtons);
      };
    }
  }, [articles]);

  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const scrollAmount = scrollRef.current.clientWidth * 0.8;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  // Don't render if no articles
  if (!isLoading && articles.length === 0) {
    return null;
  }

  // Loading state
  if (isLoading) {
    return (
      <section className="mb-8">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="w-5 h-5 text-indigo-600" />
          <h2 className="text-lg font-semibold text-gray-900">Aktuelles aus dem GoodLife Magazin</h2>
          <span className="ml-1 px-2 py-0.5 text-xs font-medium bg-indigo-100 text-indigo-700 rounded-full">...</span>
        </div>
      </section>
    );
  }

  return (
    <section className="mb-8 -mx-4 sm:-mx-6 lg:-mx-8">
      {/* Clickable Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center justify-between w-full px-4 sm:px-6 lg:px-8 mb-2 cursor-pointer group"
      >
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-indigo-600" />
          <h2 className="text-lg font-semibold text-gray-900">Aktuelles aus dem GoodLife Magazin</h2>
          <span className="ml-1 px-2 py-0.5 text-xs font-medium bg-indigo-100 text-indigo-700 rounded-full">
            {articles.length}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {/* Scroll Buttons - Desktop, only when expanded */}
          {isExpanded && (
            <div className="hidden sm:flex items-center gap-2 mr-2">
              <span
                role="button"
                onClick={(e) => { e.stopPropagation(); scroll('left'); }}
                className={`p-2 rounded-full border border-gray-200 hover:bg-gray-50 transition ${!canScrollLeft ? 'opacity-30 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                <ChevronLeft className="w-4 h-4" />
              </span>
              <span
                role="button"
                onClick={(e) => { e.stopPropagation(); scroll('right'); }}
                className={`p-2 rounded-full border border-gray-200 hover:bg-gray-50 transition ${!canScrollRight ? 'opacity-30 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                <ChevronRight className="w-4 h-4" />
              </span>
            </div>
          )}
          {isExpanded ? (
            <ChevronUp className="w-5 h-5 text-gray-400 group-hover:text-gray-600 transition" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400 group-hover:text-gray-600 transition" />
          )}
        </div>
      </button>

      {/* Collapsible Scrollable Content */}
      <div
        className="overflow-hidden transition-all duration-300 ease-in-out"
        style={{ maxHeight: isExpanded ? '500px' : '0px', opacity: isExpanded ? 1 : 0 }}
      >
        <div
          ref={scrollRef}
          className="flex gap-4 overflow-x-auto scrollbar-hide px-4 sm:px-6 lg:px-8 pb-2 pt-2 snap-x snap-mandatory"
          style={{ scrollSnapType: 'x mandatory' }}
        >
          {articles.map((article, index) => (
            <div
              key={article.id}
              className="flex-shrink-0 w-[85%] sm:w-80 snap-start"
            >
              <ArticleCard
                article={article}
                variant={index === 0 && articles.length > 1 ? 'default' : 'default'}
                onClick={() => onArticleClick?.(article)}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Custom scrollbar hide style */}
      <style>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </section>
  );
}

export default FeaturedContent;
